import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';

// Prime React Components
import { classNames } from 'primereact/utils';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from 'primereact/fileupload';
import { Dropdown } from 'primereact/dropdown';

import { isTrue } from '../utilities/CommonSnippets'

// Needed URLS
import { GET_NEURELISMICRO_HOMEPAGES, ADD_NEURELISMICRO_HOMEPAGE, EDIT_NEURELISMICRO_HOMEPAGE, DELETE_NEURELISMICRO_HOMEPAGE, NEURELISMICRO_IMAGE_PATH} from '../constants/URLS';
const GET_ENTITY = GET_NEURELISMICRO_HOMEPAGES
const ADD_ENTITY = ADD_NEURELISMICRO_HOMEPAGE
const EDIT_ENTITY = EDIT_NEURELISMICRO_HOMEPAGE
const DELETE_ENTITY = DELETE_NEURELISMICRO_HOMEPAGE

// Needed Components
const formData = new FormData();



const HomepageImages = (props) => {

	// PAGE REFS
	const toast = useRef(null);
    const dt = useRef(null);
	const fileUploadRef = useRef(null);

	// CONSTANTS
    const emptyEntity = {
        homepageImageID: null,
        photo: '', 
		altText: '', 
		openNewWindow: true,
		url: '',
		isActive: 1,
		bannerOrder: 1
    };


	// STATE VARIABLES
    const [ entities, setEntities ] = useState([]); // holds all the existing 
	const [ entity, setEntity ] = useState(emptyEntity); // current entity that we are adding or editing
	const [ entityDialog, setEntityDialog ] = useState(false);
    const [ deleteEntityDialog, setDeleteEntityDialog ] = useState(false);
    const [ submitted, setSubmitted ] = useState(false);
    const [ globalFilterValue1, setGlobalFilterValue1 ] = useState('');
	const [ filters, setFilters ] = useState({
        'global': { value: null, matchMode: FilterMatchMode.CONTAINS }
		
    });

	// USE EFFECTS HOOKS
    useEffect(() => {
		fncGet()
    }, []); 

	// ACTION / XHR FUNCTIONS
	const fncGet = async () => {
		let strURL = GET_ENTITY
		let entityXHR = await axios({method: 'get', url: strURL})
		// need to do some slight data modifications for the form

		if (entityXHR.data.success) {
			setEntities(entityXHR.data.results.map(cur => {
				return {
					...cur
				}
			}))
		} else {
			toast.current.show({ severity: 'error', summary: 'Error', detail: entityXHR.data.message, life: 3000 });
		}
	};

	const fncSave = async () => {
        setSubmitted(true);
		
		let _entity = {...entity};
		const formData = new FormData();
		formData.append("isActive", _entity.isActive);
		formData.append("url", _entity.url);
		formData.append("openNewWindow", _entity.openNewWindow);
		formData.append("bannerOrder", _entity.bannerOrder);
		formData.append("altText", _entity.altText);

		let objCurUploader = fileUploadRef.current
		let arrFiles = objCurUploader.getFiles() || []
		if (arrFiles[0]) {
			formData.append("file", arrFiles[0]);
		}


		if (_entity.homepageImageID) {
			formData.append("homepageImageID", _entity.homepageImageID);
			let editResults = await axios({
				method: 'post', 
				url: EDIT_ENTITY,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (editResults.data.success) {
				await fncGet()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Homepage Banner Edited', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: editResults.data.message, life: 3000 });
			}
		} else {

			let addResults = await axios({
				method: 'post', 
				url: ADD_ENTITY,
				data: formData,
				headers: { "Content-Type": "multipart/form-data" }
			})

			if (addResults.data.success) {
				await fncGet()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Homepage Banner Created', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: addResults.data.message, life: 3000 });
			}
		}
		
		setSubmitted(false);
		setEntityDialog(false);
		clearEntity()
	}

	const fncDelete = async () => { 
		try {
			let deleteResults = await axios({
				method: 'post', 
				url: DELETE_ENTITY,
				data: {...entity}
			})
			if (deleteResults.data.success) {
				let _entities = entities.filter(val => val.homepageImageID !== entity.homepageImageID);
				setEntities(_entities);
				setDeleteEntityDialog(false);
				clearEntity()
				toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Homepage Image Deleted', life: 3000 });
			} else {
				toast.current.show({ severity: 'error', summary: 'Error', detail: deleteResults.data.message, life: 3000 });
			}
		} catch (err) {
			toast.current.show({ severity: 'error', summary: 'Error', detail: 'There was an error deleting.', life: 3000 });
			console.error(err)
		}
	}
	
	
	// OTHER FUNCTIONS
	const clearEntity = () => {
		setEntity(emptyEntity); 
	}
	
	const openNew = () => {
		clearEntity()
		setSubmitted(false);
		setEntityDialog(true);
	}

	const hideDialog = () => {
        setSubmitted(false);
        setEntityDialog(false);
    }

	const onGlobalFilterChange1 = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue1(value);
    }

	const hideDeleteEntityDialog = () => {
        setDeleteEntityDialog(false);
    }

	const onInputChange = (value, name) => {
        let _entity = {...entity};
        _entity[`${name}`] = value;
        setEntity(_entity);
    }

	const fncEdit = (entity) => {
		setEntity(entity);
        setEntityDialog(true);
    }

	const confirmDeleteEntity = (entity) => {
        setEntity(entity);
        setDeleteEntityDialog(true);
    }

	// TABLE COLUMN TEMPLATES (table provides rowdata and column info, can draw how needed)
	const actionBodyTemplate = (rowData) => {
        return (
            <>
				<Button icon="pi pi-pencil" className="p-button-rounded p-button-success mr-2" onClick={() => fncEdit(rowData)} />
                <Button icon="pi pi-trash" className="p-button-rounded p-button-warning" onClick={() => confirmDeleteEntity(rowData)} />
            </>
        );
    }

	const photoTemplate = (rowData) => {
		return (
			<div className="flex photoCard">
				
				<div className="flex flex-column">
					<div className="flex ">
						<img src={NEURELISMICRO_IMAGE_PATH + "homePage/" + rowData.photo} />
					</div>
					{rowData.altText !== "" && 
						<div className="flex justify-content-center text-sm font-bold">
							{rowData.altText}
						</div>
					}
				</div>
			</div>
		)
	}

	const activeTemplate = (rowData) => {
       if (rowData.isActive ===1 ) return <div className="green">Active</div>
	   else return <div className="red">Inactive</div>
    }

	const urlTemplate = (rowData) => {
		if (rowData.url === "") return <div>N/A</div>

		return (
			<div className="flex flex-column">
				<div>
					{rowData.url}
				</div>
				{rowData.openNewWindow === 1 && 
					<div className="text-sm font-bold">
						(New Tab)
					</div>
				}
			</div>
		)
	 }

	// OTHER TEMPLATES (JSX)
	const leftToolbarTemplate = () => {
        return <Button label="Add New Photo" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
    }

	const rightToolbarTemplate = () => {
        return null
    }
	
	const header = (
        <div className="table-header flex justify-content-between" >
            <h5 className="mx-0 my-1" >Manage Homepage Photos</h5>
			
        </div>
    );

	const entityDialogFooter = (
        <>
            <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Save" icon="pi pi-check" className="p-button-text" onClick={fncSave} />
        </>
    );

	

    const deleteEntityDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteEntityDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={fncDelete} />
        </>
    );


	const fileHeaderTemplate = (options) => {
        const { className, chooseButton } = options;
        return (
            <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
                {chooseButton}
            </div>
        );
    };

	 return (
        <div className="datatable-resources">
             <Toast ref={toast} />

			<div className="card" style={{width:"100%"}}>
				<Toolbar className="mb-4" left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

				<DataTable ref={dt} value={entities} sortField="startDate" sortOrder={-1}  
					dataKey="homepageImageID" paginator rows={10} rowsPerPageOptions={[5, 10, 25]}
					paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
					currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Photos"
					globalFilterFields={['photo']}
					filters={filters} header={header} responsiveLayout="stack">
						<Column field="bannerOrder" sortable header="Order" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="isActive" sortable body={activeTemplate} header="Active" style={{ width: '10%', verticalAlign:'top' }}></Column>
						<Column field="photo" header="Home Page Slider Image" body={photoTemplate} style={{ width: '45%', verticalAlign:'top' }}></Column>
						<Column field="url"  header="URL Link" body={urlTemplate} style={{ width: '25%', verticalAlign:'top' }}></Column>
						<Column body={actionBodyTemplate} exportable={false} style={{ width: '10%', verticalAlign:'top' }}></Column>
				</DataTable>
            </div>

			<Dialog visible={entityDialog} style={{ width: '850px' }} header="Photo Details" modal className="p-fluid" footer={entityDialogFooter} onHide={hideDialog}>
                <div className="flex flex-column inputForm">
					<div className="inputFormFields">
						<div className="flex justify-content-between">
							<div className="field oneThirdField">
								<label htmlFor="isActive">Active</label>
								<Dropdown value={entity.isActive} onChange={(e) => onInputChange(e.value,'isActive')} options={[{label: 'Active', value: 1},{label: 'Inactive',value: 0}]}  
									placeholder="Select a Status" className="w-full md:w-14rem" />
							</div>
							
							<div className="field pr-4 oneThirdField"  >
								<label htmlFor="bannerOrder">Banner Order</label>
								<InputText id="bannerOrder" value={entity.bannerOrder} onChange={(e) => onInputChange(e.target.value, 'bannerOrder')} />
							</div>
							
						</div>
						<div className="flex">
							<div className="field fullField"  >
								<div className="flex justify-content-between" >
									<label htmlFor="photo">Photo (1400 x 327) </label> 
									{entity.homepageImageID > 0 && 
										<div className="eventMessage">
											Leave blank to keep current image
										</div>
									}
								</div>
								<FileUpload 
									name="photo" 
									ref={fileUploadRef} 
									accept="image/*" 
									maxFileSize={3000000} 
									headerTemplate={fileHeaderTemplate}
									emptyTemplate={<p className="m-0">Drag and drop files to here to upload.</p>} />
								
							</div>
						</div>
						<div className="flex">
							<div className="field fullField" >
								<div className="flex justify-content-between">
									<label htmlFor="url">URL</label>
									<div className="flex mb-2">
										<label htmlFor="openNewWindow">Open In New Tab</label>
										<Checkbox className="flex justify-content-end ml-2" inputId="openNewWindow" checked={isTrue(entity.openNewWindow)} onChange={e => onInputChange(e.checked, 'openNewWindow')} />
									</div>
								</div>
								<InputText id="url" value={entity.url} onChange={(e) => onInputChange(e.target.value, 'url')} />
							</div>
						</div>
						<div className="flex">
							<div className="field fullField" >
								<label htmlFor="altText">Alt Text</label>
								<InputText id="altText" value={entity.altText} onChange={(e) => onInputChange(e.target.value, 'altText')} />
							</div>
						</div>
						
						
					</div>
				</div>
            </Dialog>
			<Dialog visible={deleteEntityDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteEntityDialogFooter} onHide={hideDeleteEntityDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem'}} />
                    {entity && <span>Are you sure you want to delete?</span>}
                </div>
            </Dialog>
        </div>
    );
}

export default HomepageImages;